import { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API_URL } from "../../config";
import LoadingSpinner from "../../components/LoadingSpinner";
import { showToast } from "../../components/toast";

const PayrollReport = () => {
  const [payrollReport, setPayrollReport] = useState([]);
  const [monthList, setMonthList] = useState([]);
  const [selectedStartMonth, setSelectedStartMonth] = useState("");
  const [selectedEndMonth, setSelectedEndMonth] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);

  const token = localStorage.getItem("authToken");

  useEffect(() => {
    fetchMonthList();
  }, []);

  const handleExport = async () => {
    if (!selectedStartMonth || !selectedEndMonth) {
      showToast.error("Please select both start month and end month before exporting");
      return;
    }

    try {
      setExporting(true);
      setError("");

      const response = await axios.post(
        `${API_URL}payroll/payroll-report/export-report`,
        {
          start_month: selectedStartMonth,
          end_month: selectedEndMonth,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Payroll_Report_${selectedStartMonth}_to_${selectedEndMonth}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showToast.success("Report exported successfully!");
    } catch (error) {
      console.error("Export error", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to export report";
      setError(errorMessage);
      showToast.error(errorMessage);
    } finally {
      setExporting(false);
    }
  };

  const fetchReport = (e, eventName = "submit") => {
    if (e?.preventDefault) {
      e.preventDefault();
    }

    if (!selectedStartMonth || !selectedEndMonth) {
      showToast.error("Please select both start month and end month first");
      return;
    }

    setLoading(true);
    setError("");

    const actionQuery = eventName === "submit" ? "submit=true" : "clearall=true";

    axios
      .get(
        `${API_URL}payroll/payroll-report/index?start_month=${selectedStartMonth}&end_month=${selectedEndMonth}&${actionQuery}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      )
      .then((response) => {
        const reportData = response.data.reportData || [];
        setPayrollReport(reportData);
        setLoading(false);
        if (reportData && reportData.length > 0) {
          showToast.success("Report loaded successfully!");
        } else {
          showToast.info("No data found for selected month");
        }
      })
      .catch((err) => {
        const errorMessage =
          err.response?.data?.message ||
          err.message ||
          "Error fetching payroll data";
        setError(errorMessage);
        setLoading(false);
        showToast.error(errorMessage);
      });
  };

  const fetchMonthList = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}payroll/salary-report/month-list`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      let list = [];
      if (response.data?.monthlist) {
        list = response.data.monthlist;
      } else if (Array.isArray(response.data)) {
        list = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        list = response.data.data;
      }

      if (!Array.isArray(list) || list.length === 0) {
        setError("No months available or invalid format");
        return;
      }

      const formattedList = list.map((item, index) => ({
        name: item.label || `Month ${item.value}`,
        value: item.value,
      }));

      setMonthList(formattedList);
      setError("");
    } catch (err) {
      console.error("Failed to load months", err);
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to load month list";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleMonthChange = (field) => (e) => {
    const value = e.target.value;
    setError("");
    setPayrollReport([]);

    if (field === "start") {
      setSelectedStartMonth(value);
      return;
    }

    if (field === "end") {
      setSelectedEndMonth(value);
    }
  };

  const renderTableRows = () => {
    return payrollReport.map((report, index) => {
      return (
        <tr key={index}>
          <td>{report.employee_name}</td>
          <td>{report.pan_number}</td>
          <td>{report.payroll_month}</td>
          <td>{report.total_earned_salary}</td>
          <td>{report.tds}</td>
          <td>{report.pf}</td>
          <td>{report.pt}</td>
          <td>{report.other_deductions}</td>
          <td>{report.net_salary_paid}</td>
        </tr>
      );
    });
  };

  const handleClearAll = (e, eventName = "clearall") => {
    if (e?.preventDefault) {
      e.preventDefault();
    }

    setSelectedStartMonth("");
    setSelectedEndMonth("");
    setPayrollReport([]);
    setError("");
    showToast.info(`All data cleared (${eventName})`);
  };

  return (
    <div
      id="page-container"
      className="page-header-fixed page-sidebar-fixed fade in"
    >
      <div id="content" className="">
        {loading && <LoadingSpinner />}
        <div className="pb-2 d-flex justify-content-between align-items-center">
          <div className="">
            <ul className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/">
                  <i className="ti ti-home"></i> Home
                </Link>
              </li>
              <li className="breadcrumb-item active">Payroll Report</li>
            </ul>
          </div>
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-default btn-sm"
              onClick={handleExport}
              disabled={!selectedStartMonth || !selectedEndMonth || exporting}
            >
              {exporting ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-1"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Exporting...
                </>
              ) : (
                <>
                  <i className="ti ti-export"></i> Export Report
                </>
              )}
            </button>
          </div>
        </div>

        <div className="panel panel-default with-rounded-corner with-shadow">
          <div className="panel-heading border-0 pt-3">
            <span className="panel-title">Payroll Report</span>
          </div>
          <div className="panel-body">
            <div className="row">
              <div className="col-md-3">
                <div className="form-group">
                  <label className="control-label">Start Month</label>
                  <select
                    className="form-control"
                    value={selectedStartMonth}
                    onChange={handleMonthChange("start")}
                  >
                    <option value="">Select Month</option>
                    {monthList.length > 0 ? (
                      monthList.map((month, idx) => (
                        <option key={idx} value={month.value}>
                          {month.name}
                        </option>
                      ))
                    ) : (
                      <option disabled>Loading months...</option>
                    )}
                  </select>
                </div>
              </div>
              <div className="col-md-3">
                <div className="form-group">
                  <label className="control-label">End Month</label>
                  <select
                    className="form-control"
                    value={selectedEndMonth}
                    onChange={handleMonthChange("end")}
                  >
                    <option value="">Select Month</option>
                    {monthList.length > 0 ? (
                      monthList.map((month, idx) => (
                        <option key={idx} value={month.value}>
                          {month.name}
                        </option>
                      ))
                    ) : (
                      <option disabled>Loading months...</option>
                    )}
                  </select>
                </div>
              </div>
              <div className="col-md-5">
                <div style={{ float: "left", marginTop: "25px" }}>
                  <button
                    type="button"
                    onClick={(e) => fetchReport(e, "submit")}
                    className="btn btn-primary"
                  >
                    Submit
                  </button>
                </div>
                <div
                  style={{
                    float: "left",
                    marginTop: "25px",
                    paddingLeft: "10px",
                  }}
                >
                  <button
                    type="button"
                    onClick={(e) => handleClearAll(e, "clearall")}
                    className="btn btn-danger"
                  >
                    Clear All
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        {payrollReport.length > 0 ? (
          <div className="table-responsive panel panel-default with-rounded-corner tbl_head_sticky tbl_col_sticky with-shadow">
            <table className="table table-condensed table-bordered bg-white">
              <thead>
                <tr>
                  <th className="text-center">Name of the employee</th>
                  <th className="text-center">PAN number</th>
                  <th className="text-center">Payroll Month</th>
                  <th className="text-center">Total Earned Salary</th>
                  <th className="text-center">TDS</th>
                  <th className="text-center">PF</th>
                  <th className="text-center">PT</th>
                  <th className="text-center">Other Deductions</th>
                  <th className="text-center">Net Salary Paid</th>
                </tr>
              </thead>
              <tbody>{renderTableRows()}</tbody>
            </table>
          </div>
        ) : (
          <>
            <div className="clearfix"></div>
            <div className="panel-body">
              <p>No Record(s) added yet.</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PayrollReport;
